import React, { useState } from 'react';
import {
  CheckCircle2,
  XCircle,
  RefreshCw,
  Clock,
  Layers,
  GitBranch,
  Zap,
  Database,
  ChevronDown,
  ChevronUp,
  Shield,
  Scale,
  Lock,
  AlertTriangle
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────
// ReviewGate
// ─────────────────────────────────────────────────────────────
const ReviewGate = ({ execution, onApprove, onReject, onRegenerate, loading }) => {
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const handleReject = () => {
    if (rejectReason.trim()) {
      onReject(rejectReason);
      setShowRejectModal(false);
      setRejectReason('');
    }
  };

  if (execution.status === 'approved') {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-green-700">
          <CheckCircle2 className="w-5 h-5" />
          <span className="font-medium">Execution approvata</span>
        </div>
      </div>
    );
  }

  if (execution.status === 'rejected') {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-red-700">
          <XCircle className="w-5 h-5" />
          <span className="font-medium">Execution rigettata</span>
        </div>
        {execution.rejectionReason && (
          <p className="text-sm text-red-600 mt-2">{execution.rejectionReason}</p>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="font-medium text-gray-800 mb-3">Revisiona Output</h3>
        <div className="flex gap-3">
          <button onClick={() => onApprove()} disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50">
            <CheckCircle2 className="w-4 h-4" /> Approva
          </button>
          <button onClick={onRegenerate} disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50">
            <RefreshCw className="w-4 h-4" /> Rigenera
          </button>
          <button onClick={() => setShowRejectModal(true)} disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50">
            <XCircle className="w-4 h-4" /> Rigetta
          </button>
        </div>
      </div>

      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Motivo del Rigetto</h3>
            <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Spiega perche rigetti questa execution..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              rows={4} />
            <div className="flex gap-3 mt-4">
              <button onClick={() => setShowRejectModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                Annulla
              </button>
              <button onClick={handleReject} disabled={!rejectReason.trim()}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">
                Conferma Rigetto
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ─────────────────────────────────────────────────────────────
// StringList
// ─────────────────────────────────────────────────────────────
const StringList = ({ items }) => {
  if (!items || items.length === 0) return <p className="text-gray-400 text-sm italic">Nessun elemento</p>;
  return (
    <ul className="space-y-1">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2 text-sm">
          <span className="mt-1 w-1.5 h-1.5 rounded-full bg-gray-400 flex-shrink-0" />
          <span className="text-gray-700">{typeof item === 'string' ? item : JSON.stringify(item)}</span>
        </li>
      ))}
    </ul>
  );
};

// ─────────────────────────────────────────────────────────────
// StructureFamilyCard
// ─────────────────────────────────────────────────────────────
const StructureFamilyCard = ({ family, index }) => {
  const [expanded, setExpanded] = useState(index === 0);

  const hasHigherOrder = family.higher_order_robustness
    || family.evidence_independence
    || family.autonomy_preservation
    || (family.second_order_failure_modes?.length > 0);

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3 text-left">
          <span className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full bg-lime-100 text-lime-700 font-bold text-sm">
            {index + 1}
          </span>
          <div>
            <h4 className="font-semibold text-gray-800">{family.name || `Structure Family ${index + 1}`}</h4>
            {!expanded && family.description && (
              <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{family.description}</p>
            )}
          </div>
          {hasHigherOrder && (
            <span className="ml-2 px-1.5 py-0.5 text-xs bg-violet-100 text-violet-700 rounded font-medium">HO</span>
          )}
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
      </button>

      {expanded && (
        <div className="border-t border-gray-100 p-4 space-y-3 bg-gray-50">
          {family.description && (
            <div>
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Description</span>
              <p className="text-sm text-gray-700 mt-1 leading-relaxed">{family.description}</p>
            </div>
          )}
          {family.inferential_mechanism && (
            <div>
              <div className="flex items-center gap-1 mb-1">
                <GitBranch className="w-3 h-3 text-lime-600" />
                <span className="text-xs font-semibold text-lime-700 uppercase tracking-wide">Inferential Mechanism</span>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">{family.inferential_mechanism}</p>
            </div>
          )}
          {family.compatibility_with_latent_state && (
            <div>
              <div className="flex items-center gap-1 mb-1">
                <Zap className="w-3 h-3 text-teal-600" />
                <span className="text-xs font-semibold text-teal-700 uppercase tracking-wide">Compatibility with Latent State</span>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">{family.compatibility_with_latent_state}</p>
            </div>
          )}
          {family.compatibility_with_prior_transition && (
            <div>
              <div className="flex items-center gap-1 mb-1">
                <Zap className="w-3 h-3 text-blue-600" />
                <span className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Compatibility with Prior Transition</span>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">{family.compatibility_with_prior_transition}</p>
            </div>
          )}
          {family.compatibility_with_policy && (
            <div>
              <div className="flex items-center gap-1 mb-1">
                <Zap className="w-3 h-3 text-green-600" />
                <span className="text-xs font-semibold text-green-700 uppercase tracking-wide">Compatibility with Policy</span>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">{family.compatibility_with_policy}</p>
            </div>
          )}
          {family.prediction_error_pattern && (
            <div>
              <div className="flex items-center gap-1 mb-1">
                <Database className="w-3 h-3 text-blue-600" />
                <span className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Prediction Error Pattern</span>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">{family.prediction_error_pattern}</p>
            </div>
          )}
          {family.evidence_organization && (
            <div>
              <div className="flex items-center gap-1 mb-1">
                <Layers className="w-3 h-3 text-purple-600" />
                <span className="text-xs font-semibold text-purple-700 uppercase tracking-wide">Evidence Organization</span>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">{family.evidence_organization}</p>
            </div>
          )}

          {/* Higher-Order Structural Robustness */}
          {hasHigherOrder && (
            <div className="mt-3 pt-3 border-t border-violet-100 space-y-3">
              <div className="flex items-center gap-1.5">
                <Lock className="w-3 h-3 text-violet-600" />
                <span className="text-xs font-semibold text-violet-700 uppercase tracking-wide">Higher-Order Structural Robustness</span>
              </div>

              {family.higher_order_robustness && (
                <div className="rounded-lg bg-violet-50 border border-violet-200 p-3">
                  <div className="flex items-center gap-1 mb-1">
                    <Shield className="w-3 h-3 text-violet-600" />
                    <span className="text-xs font-semibold text-violet-700 uppercase tracking-wide">Higher-Order Robustness</span>
                  </div>
                  <p className="text-sm text-gray-700 leading-relaxed">{family.higher_order_robustness}</p>
                </div>
              )}

              {family.evidence_independence && (
                <div className="rounded-lg bg-indigo-50 border border-indigo-200 p-3">
                  <div className="flex items-center gap-1 mb-1">
                    <Scale className="w-3 h-3 text-indigo-600" />
                    <span className="text-xs font-semibold text-indigo-700 uppercase tracking-wide">Evidence Independence</span>
                  </div>
                  <p className="text-sm text-gray-700 leading-relaxed">{family.evidence_independence}</p>
                </div>
              )}

              {family.autonomy_preservation && (
                <div className="rounded-lg bg-teal-50 border border-teal-200 p-3">
                  <div className="flex items-center gap-1 mb-1">
                    <Scale className="w-3 h-3 text-teal-600" />
                    <span className="text-xs font-semibold text-teal-700 uppercase tracking-wide">Autonomy Preservation</span>
                  </div>
                  <p className="text-sm text-gray-700 leading-relaxed">{family.autonomy_preservation}</p>
                </div>
              )}

              {family.second_order_failure_modes?.length > 0 && (
                <div className="rounded-lg bg-red-50 border border-red-200 p-3">
                  <div className="flex items-center gap-1 mb-2">
                    <AlertTriangle className="w-3 h-3 text-red-600" />
                    <span className="text-xs font-semibold text-red-700 uppercase tracking-wide">Second-Order Failure Modes</span>
                  </div>
                  <StringList items={family.second_order_failure_modes} />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// EmptyState
// ─────────────────────────────────────────────────────────────
const EmptyState = () => (
  <div className="flex items-center justify-center h-full">
    <div className="text-center text-gray-500">
      <Clock className="w-12 h-12 mx-auto mb-4 text-gray-300" />
      <p className="text-lg font-medium mb-2">In attesa di esecuzione</p>
      <p className="text-sm">Clicca "Esegui Contract" per avviare la Structural Creative Taxonomy</p>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────
// Contract05View
// ─────────────────────────────────────────────────────────────
const Contract05View = ({ execution, onApprove, onReject, onRegenerate, loading }) => {
  if (!execution) return <EmptyState />;

  const output = execution.output || execution.outputData || {};
  const families = output.structure_families || [];

  const hoCount = families.filter(f =>
    f.higher_order_robustness || f.evidence_independence || f.autonomy_preservation
  ).length;

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="max-w-4xl mx-auto space-y-6">

        {/* Header */}
        <div>
          <div className="flex items-center gap-3 mb-1">
            <Layers className="w-6 h-6 text-lime-600" />
            <h2 className="text-xl font-bold text-gray-800">Structural Creative Taxonomy</h2>
            <span className="px-2 py-0.5 text-xs bg-lime-100 text-lime-700 rounded-full font-medium">C05</span>
          </div>
          <p className="text-sm text-gray-500">Famiglie strutturali compatibili con il Creative Latent State</p>
        </div>

        {/* Stats */}
        <div className="bg-lime-50 border border-lime-200 rounded-lg p-4">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-3">
              <Layers className="w-8 h-8 text-lime-600" />
              <div>
                <div className="text-2xl font-bold text-lime-700">{families.length}</div>
                <div className="text-sm text-lime-600">Structure Families identificate</div>
              </div>
            </div>
            {hoCount > 0 && (
              <div className="flex items-center gap-3 border-l border-lime-200 pl-6">
                <Shield className="w-8 h-8 text-violet-500" />
                <div>
                  <div className="text-2xl font-bold text-violet-600">{hoCount}</div>
                  <div className="text-sm text-violet-500">Con robustezza higher-order</div>
                </div>
              </div>
            )}
            <div className="ml-auto text-xs text-lime-600 text-right">
              <p>Architetture inferenziali riutilizzabili</p>
              <p>Indipendenti da prodotto, attore, paese e lingua</p>
            </div>
          </div>
        </div>

        {/* Structure Families */}
        {families.length > 0 ? (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Structure Families</h3>
            {families.map((family, i) => (
              <StructureFamilyCard key={i} family={family} index={i} />
            ))}
          </div>
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center text-gray-500">
            <Layers className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">Nessuna structure family trovata nell'output</p>
          </div>
        )}

        {/* Review Gate */}
        <ReviewGate
          execution={execution}
          onApprove={onApprove}
          onReject={onReject}
          onRegenerate={onRegenerate}
          loading={loading}
        />
      </div>
    </div>
  );
};

export default Contract05View;
